import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Loader2,
  Search,
  MapPin,
  Calendar,
  Building2,
  AlertCircle,
  Shield,
  Briefcase,
  Clock,
  Filter,
  CheckCircle2
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { formatDate } from '../../utils/dateFormatter';

const isEOIDate = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr).getFullYear() > 2050;
};

const CandidateJobBoard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rfqs, setRfqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [appliedRfqs, setAppliedRfqs] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedClearances, setSelectedClearances] = useState([]);
  const [selectedWorkArrangements, setSelectedWorkArrangements] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setError('Not authenticated');
        return;
      }

      // Fetch candidate ID
      const { data: candidate } = await supabase
        .from('candidates')
        .select('id')
        .eq('user_id', user.id)
        .single();

      // Fetch visible RFQs
      const { data: rfqsData, error: rfqsError } = await supabase
        .from('rfqs')
        .select(`
          *,
          client:clients(name)
        `)
        .eq('display_status', 'visible')
        .order('created_at', { ascending: false });

      if (rfqsError) throw rfqsError;

      setRfqs(rfqsData || []);

      // Fetch applied RFQs
      if (candidate) {
        const { data: applications } = await supabase
          .from('applications')
          .select('rfq_id')
          .eq('candidate_id', candidate.id);

        const appliedSet = new Set(applications?.map(app => app.rfq_id) || []);
        setAppliedRfqs(appliedSet);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique filter options from data
  const filterOptions = useMemo(() => {
    const locations = new Set();
    const clearances = new Set();
    const workArrangements = new Set();

    rfqs.forEach((rfq) => {
      if (rfq.locations && Array.isArray(rfq.locations)) {
        rfq.locations.forEach((loc) => locations.add(loc));
      }
      if (rfq.clearance_required) clearances.add(rfq.clearance_required);
      if (rfq.work_arrangement) workArrangements.add(rfq.work_arrangement);
    });

    return {
      locations: Array.from(locations).sort(),
      clearances: Array.from(clearances).sort(),
      workArrangements: Array.from(workArrangements).sort(),
    };
  }, [rfqs]);

  // Filter jobs based on search and filters
  const filteredRfqs = useMemo(() => {
    let filtered = rfqs;

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (rfq) =>
          rfq.title?.toLowerCase().includes(search) ||
          rfq.ref_code?.toLowerCase().includes(search) ||
          rfq.client?.name?.toLowerCase().includes(search) ||
          rfq.description?.toLowerCase().includes(search)
      );
    }

    // Location filter
    if (selectedLocations.length > 0) {
      filtered = filtered.filter((rfq) =>
        rfq.locations?.some((loc) => selectedLocations.includes(loc))
      );
    }

    // Clearance filter
    if (selectedClearances.length > 0) {
      filtered = filtered.filter((rfq) =>
        selectedClearances.includes(rfq.clearance_required)
      );
    }

    // Work arrangement filter
    if (selectedWorkArrangements.length > 0) {
      filtered = filtered.filter((rfq) =>
        selectedWorkArrangements.includes(rfq.work_arrangement)
      );
    }

    return filtered;
  }, [rfqs, searchTerm, selectedLocations, selectedClearances, selectedWorkArrangements]);

  const toggleFilter = (value, setter, currentValues) => {
    setter(
      currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value]
    );
  };

  const clearAllFilters = () => {
    setSelectedLocations([]);
    setSelectedClearances([]);
    setSelectedWorkArrangements([]);
    setSearchTerm('');
  };

  const activeFiltersCount =
    selectedLocations.length + selectedClearances.length + selectedWorkArrangements.length;

  const handleApply = (rfqId) => {
    navigate(`/job/${rfqId}/apply`);
  };

  const handleViewDetails = (rfqId) => {
    navigate(`/job/${rfqId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading available positions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Job Board</h1>
        <p className="text-muted-foreground mt-2">
          Browse and apply for available positions
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Search and Filter Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="search"
            placeholder="Search jobs by title, ref code, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setShowFilters(!showFilters)}
          className="md:w-auto"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filters
          {activeFiltersCount > 0 && (
            <Badge variant="default" className="ml-2">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Location Filter */}
              {filterOptions.locations.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Location</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filterOptions.locations.map((location) => (
                      <Label key={location} className="flex items-center gap-2 font-normal cursor-pointer">
                        <Checkbox
                          checked={selectedLocations.includes(location)}
                          onCheckedChange={() =>
                            toggleFilter(location, setSelectedLocations, selectedLocations)
                          }
                        />
                        {location}
                      </Label>
                    ))}
                  </div>
                </div>
              )}

              {/* Clearance Filter */}
              {filterOptions.clearances.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Security Clearance</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filterOptions.clearances.map((clearance) => (
                      <Label key={clearance} className="flex items-center gap-2 font-normal cursor-pointer">
                        <Checkbox
                          checked={selectedClearances.includes(clearance)}
                          onCheckedChange={() =>
                            toggleFilter(clearance, setSelectedClearances, selectedClearances)
                          }
                        />
                        {clearance}
                      </Label>
                    ))}
                  </div>
                </div>
              )}

              {/* Work Arrangement Filter */}
              {filterOptions.workArrangements.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Work Arrangement</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {filterOptions.workArrangements.map((arrangement) => (
                      <Label
                        key={arrangement}
                        className="flex items-center gap-2 font-normal cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedWorkArrangements.includes(arrangement)}
                          onCheckedChange={() =>
                            toggleFilter(
                              arrangement,
                              setSelectedWorkArrangements,
                              selectedWorkArrangements
                            )
                          }
                        />
                        {arrangement}
                      </Label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {activeFiltersCount > 0 && (
              <div className="mt-4 pt-4 border-t flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active
                </p>
                <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                  Clear all filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results Count */}
      <div>
        <p className="text-sm text-muted-foreground">
          {filteredRfqs.length} job{filteredRfqs.length !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Job Listings Grid */}
      {filteredRfqs.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredRfqs.map((rfq) => {
            const hasApplied = appliedRfqs.has(rfq.id);

            return (
              <Card key={rfq.id} className="hover:shadow-lg transition-shadow flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-lg line-clamp-2">{rfq.title}</CardTitle>
                    {hasApplied && (
                      <Badge className="bg-green-100 text-green-800 flex-shrink-0">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Applied
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    {rfq.client?.name || 'Confidential'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2 flex-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Briefcase className="w-4 h-4 flex-shrink-0" />
                    <span>{rfq.ref_code || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="line-clamp-1">
                      {rfq.locations && rfq.locations.length > 0
                        ? (Array.isArray(rfq.locations) ? rfq.locations.join(', ') : rfq.locations)
                        : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <Badge variant="secondary" className="text-xs">
                      {rfq.clearance_required || 'None'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {rfq.client_close_datetime
                        ? isEOIDate(rfq.client_close_datetime)
                          ? 'Rolling — No closing date'
                          : `Closes: ${formatDate(rfq.client_close_datetime, { style: 'short' })}`
                        : 'No close date'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    <span>
                      {rfq.initial_term_months
                        ? `${rfq.initial_term_months} month${rfq.initial_term_months !== 1 ? 's' : ''}${rfq.extension_options ? ` + ${rfq.extension_options}` : ''}`
                        : '—'}
                    </span>
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleViewDetails(rfq.id)}
                  >
                    View Details
                  </Button>
                  {!hasApplied && (
                    <Button
                      size="sm"
                      className="flex-1"
                      onClick={() => handleApply(rfq.id)}
                    >
                      Apply Now
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Jobs Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || activeFiltersCount > 0
                ? 'No jobs match your search criteria. Try adjusting your filters.'
                : 'No positions are currently available'}
            </p>
            {(searchTerm || activeFiltersCount > 0) && (
              <Button onClick={clearAllFilters} variant="outline">
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CandidateJobBoard;
